'use client'
import React, { useEffect, useCallback } from 'react'
import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import SvgIcon from '../svg-icons/svg-icons'
import { ViewPort } from '@/enum/enum'

export type SubMenuItem = {
  name?: string | null | undefined
  url?: string | null | undefined
  id?: string | null | undefined
}

const CHevronUpSvg = (
  <SvgIcon
    width="12"
    height="12"
    fill="currentColor"
    className="bi bi-chevron-up text-sm/6 font-bold relative xl:left-[4px] left:left-0"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
    />
  </SvgIcon>
)
const ChevronDownSvg = (
  <SvgIcon
    width="12"
    height="12"
    fill="currentColor"
    className="bi bi-chevron-down text-sm/6 relative xl:left-[4px] left:left-0"
    viewBox="0 0 16 16"
  >
    {' '}
    <path
      fillRule="evenodd"
      d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
    />
  </SvgIcon>
)

interface IDropdownComponentProps {
  menuItems: SubMenuItem[] | null | undefined
  title: string
  onItemClick: () => void
  viewPort: ViewPort
  isOpen: boolean
  toggleDropdown: (isOpen: boolean) => void
  keyValue?: string
}

const DropdownComponent = ({
  menuItems,
  title,
  onItemClick,
  viewPort,
  isOpen,
  toggleDropdown,
  keyValue,
}: IDropdownComponentProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const url = usePathname()

  // load links on dropdown by order with transition
  const toogleAnimation = useCallback(
    (isOpenMenu: boolean, viewPort = '') => {
      const selector = `.${keyValue}${viewPort} .transform a`
      const links = Array.from(document.querySelectorAll(selector)).map(
        (link) => link as HTMLElement,
      )
      // viewPort is mobile or desktop
      //The timeout value should be the number of the links multiply by 10, but also should be the same for the both keyValues [profiles and aboutus]. This way we avoid clashes.
      const timeout = 60
      const timeoutEnd = 10

      if (isOpenMenu) {
        links.forEach((link, index) => {
          setTimeout(() => {
            link.style.animation = ''
            link.classList.add('animate-in')
            link.style.opacity = '1'
          }, (index + 1) * timeout)
        })
      } else {
        ;[...links].reverse().forEach((link, index) => {
          setTimeout(() => {
            link.classList.remove('animate-in')
            link.style.opacity = '0'
          }, (index + 1) * timeoutEnd)
        })
      }
    },
    [keyValue],
  )
  useEffect(() => {
    toogleAnimation(isHovered, `.${viewPort}`)
  }, [isHovered, toogleAnimation, viewPort])

  useEffect(() => {
    toogleAnimation(isOpen, `.${viewPort}`)
  }, [isOpen, toogleAnimation, viewPort])

  const handleItemClick = () => {
    onItemClick()
  }
  return (
    <>
      {viewPort === ViewPort.DESKTOP && (
        <div
          className={`relative inline-block menuTransition ${keyValue} ${viewPort}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="text-white leading-[27px] text-lg md:text-base border-0 cursor-pointer flex items-center xl:hover:border-b xl:hover:border-feYellow xl:hover:rounded-[2px] tranform">
            {title}
            {isHovered ? CHevronUpSvg : ChevronDownSvg}
          </div>
          {
            <div
              className={`${
                isHovered ? 'opacity-100 max-h-[500px]' : 'opacity-0 max-h-0'
              } flex flex-col absolute z-[1] left-[-10px] bg-black min-w-[148px] py-0 px-[5px] border-0 my-[1px] mx-0 overflow-hidden transition-[max-height,opacity] duration-300 ease-in  transform`}
            >
              {menuItems?.map((item) => (
                <Link
                  key={item.name}
                  href={`${item.url}`}
                  className="first:my-[18px] mx-[4px] mt-0 mb-[18px] block border-0 leading-[27px] text-lg md:text-base hover:text-feYellow"
                  prefetch={false}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          }
        </div>
      )}
      {viewPort === ViewPort.MOBILE && (
        <div className={`${keyValue} ${viewPort}`}>
          <div
            className="flex flex-row items-center leading-[25.7143px]"
            onClick={() => toggleDropdown(!isOpen)}
          >
            <div className="me-[4px]"> {title} </div>
            {isOpen ? CHevronUpSvg : ChevronDownSvg}
          </div>
          {
            <div
              className={`flex flex-col left-[10px] relative overflow-hidden transition-[max-height 300ms, opacity 300ms ease-in-out] ease-in-out duration-300 max-h-0 opacity-0 ${
                isOpen ? `max-h-[500px] opacity-[10] open` : ''
              } transform`}
            >
              {menuItems?.map((item) => (
                <Link
                  key={item.name}
                  href={`${item.url}`}
                  prefetch={false}
                  className={`leading-[25.7143px] text-lg md:text-base mt-[5px] mx-[1px] mb-[0px] block ${
                    url === item.url
                      ? '!underline !decoration-feYellow !decoration-1 !underline-offset-4'
                      : ''
                  }`}
                  onClick={() => handleItemClick()}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          }
        </div>
      )}
    </>
  )
}
export default DropdownComponent
